import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import PrimeTable from "../../../components/PrimeTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import appActions from '../../../appActions';
import SysEventsFilterForm from "./SysEventsFilterForm";
import { Button } from "primereact/button";
import { isPermissionCodeValid } from "../../../utils/mics";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";
import moment from "moment";

const SysEventsList = ({ pageId }) => {
    
    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`);

    let navigate = useNavigate();
    const dispatch = useDispatch();

    const sysEventList = useSelector ((state) => state.sysEvents.sysEventsList);
    const filters = useSelector((state) => state.sysEvents.filters);

     const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
        date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        date_till: moment().format('YYYY-MM-DD'),
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
    
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [deleteRecord, setDeleteRecord] = useState(null);

    const handleFormValuesChange = (values) => {
        setFormValues(values);
    }
    
    const handleRefresh = useCallback(() => {
        dispatch(appActions.SYSEVENTS_GET_ALL_REQUEST(defaultParamsWithoutFilters));
       
    },[ dispatch, defaultParamsWithoutFilters ]);

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex"> POS Command Events </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp' data-pr-tooltip="Refresh" onClick={handleRefresh} />
            </div>
        );
    };

    const handleReset = () => {
        setFormValues(defaultParamsWithoutFilters)
        dispatch(appActions.SYSEVENTS_RESET_ACTION_REQUEST());
        if (defaultParamsWithoutFilters) {
            dispatch(appActions.SYSEVENTS_GET_ALL_REQUEST(defaultParamsWithoutFilters));
        }
    }

    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
            
            dispatch(appActions.SYSEVENTS_RESET_ACTION_REQUEST());
            if (searchParams) { 
                dispatch(appActions.SYSEVENTS_GET_ALL_REQUEST(searchParams));
            }
        },
        [dispatch, defaultParams?.pageno, defaultParams?.pagesize, defaultParams?.sort, defaultParams?.sortOrder]
    );

    useEffect(() => {
        handleSearch(defaultParams);
    }, [defaultParams]); // Empty dependency array ensures it runs only on mount

    const handleEditRedirect = useCallback((record) => {
            const data = sysEventList?.data?.find((t) => t.id === record.id);
            navigate('/ui/sys-events/details', { state: { data, isEdit: true } });
    }, [navigate, sysEventList]);
    
    
    const handleViewRedirect = useCallback((record) => {
        const data = sysEventList?.data?.find(t => t.id === record.id);
        navigate('/ui/sys-events/details', { state: { data, isView: true } });
    }, [sysEventList, navigate]);

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.SYSEVENTS_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                {readAccess && 
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                { writeAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                        <i className="pi pi-pencil text-blue-500 text-base"></i>
                    </span>
                }
                { deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, handleDelete, handleEditRedirect, readAccess, deleteAccess, writeAccess]
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );
    
    const columns = [       
        { field: 'storeId', header: 'StoreId', sortable: true, fileLog: true, width: '100px' },
        { field: 'cmdType', header: 'CMD Type', sortable: true },
        { field: 'cmdDate', header: 'CMD Date', sortable: true, width: '120px' },
        // { field: 'cmdTime', header: 'CMD Time', sortable: true},
        { field: 'cmdValidity', header: 'CMD Validity', sortable: true, width: '130px' },
        { field: 'processed', header: 'Processed', sortable: true, fileLog: true, width: '100px' },
        { field: 'processedOn', header: 'Pocessed On', sortable: true, fileLog: true, width: '130px' },
        { field: 'status', header: 'Status', sortable: true, width: '120px' },       
        { field: 'createdOn', header: 'Created On', sortable: true, width: '140px', columnType: 'datetime' },   
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];


    const renderList = () => {
        return (
            <>
                <div className='pv-15 ph-15'>
                    {renderHeader()}
                </div>
                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <SysEventsFilterForm 
                        handleSearch={handleSearch} 
                        handleReset={handleReset}  
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={sysEventList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='api_logs'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />
                </div>
            </>
        );
    };

    return <div className="form_height">
        {renderList()}
        {deleteRecord && (
            <ConfirmationDialog
                message={`Are you sure you want to delete the External Event Command  : ${deleteRecord.id}?`}
                onAccept={handleDeleteConfirm}
                onReject={() => setDeleteRecord(null)}
            />
        )}
    </div>;

}

export default SysEventsList;

